import { VerifySMS } from '@/components/compliance';
import { SenderIdList } from '@/components/sender-id';
import { Layout } from '@/components/ui/design-system';
import { useIsAdminOrManager } from '@/hooks/api/team';
import { useTranslation } from '@/hooks/use-translation';
import { useLines } from '@/providers/lines/use-lines';
import { ANALYTICS_EVENTS, analytics } from '@/services/analytics';
import { $apiAllo, $fetchAllo } from '@/services/api-allo/client';
import type { VerifiedSenderIdResponse } from '@/types/calls-types';
import { createFileRoute } from '@tanstack/react-router';
import { useCallback } from 'react';
import { toast } from 'sonner';

const RouteComponent = () => {
  const { t } = useTranslation('desktop_ui');
  const { currentLine } = useLines();
  const isSenderIdLine = currentLine.type === 'SENDER_ID';
  const isFrenchNumber = isSenderIdLine || currentLine.phoneNumber.number?.country === 'FR';
  const { isAdminOrManager, isLoading: isRoleLoading } = useIsAdminOrManager();
  const isReadOnly = !isRoleLoading && !isAdminOrManager;

  const {
    data: senderIdsData,
    isLoading: isLoadingSenderIds,
    refetch: refetchSenderIds,
  } = $apiAllo.useQuery(
    'get',
    '/v1/sender-id',
    {},
    {
      enabled: isFrenchNumber,
    },
  );

  const senderIds: VerifiedSenderIdResponse[] = senderIdsData?.data ?? [];

  const handleCreateSenderId = useCallback(
    async (data: { sender_id: string; example_content: string }) => {
      try {
        const { response, error } = await $fetchAllo.POST('/v1/sender-id', {
          body: data,
        });

        if (!response?.ok || error) {
          const errorCode = (error as { code?: string })?.code;
          throw new Error(errorCode || t('settings.compliance.senderId.createError'));
        }

        toast.success(t('settings.compliance.senderId.createSuccess'));
        refetchSenderIds();
      } catch (err) {
        // Re-throw with user-friendly message for network errors
        if (err instanceof Error && err.message) {
          throw err;
        }
        throw new Error(t('settings.compliance.senderId.createError'));
      }
    },
    [t, refetchSenderIds],
  );

  const subtitle = isFrenchNumber ? t('settings.compliance.senderId.description') : undefined;

  return (
    <Layout
      title={isFrenchNumber ? t('settings.compliance.senderId.pageTitle') : undefined}
      subtitle={subtitle}
      isScrollable
    >
      {isFrenchNumber ? (
        <SenderIdList
          senderIds={senderIds}
          isLoading={isLoadingSenderIds}
          onCreateSenderId={handleCreateSenderId}
          isReadOnly={isReadOnly}
        />
      ) : (
        <VerifySMS isReadOnly={isReadOnly} />
      )}
    </Layout>
  );
};

export const Route = createFileRoute('/_app/settings/compliance/')({
  component: RouteComponent,
  onEnter: () => analytics.track(ANALYTICS_EVENTS.COMPLIANCE.PAGE_VIEWED),
});
